/** One entry of a name and label list: `name` identifies it, `label` is what the visitor reads. */
export interface NameLabelEntry {
  name: string;
  label: string;
}

const isObject = (value: unknown): value is Record<string, unknown> => typeof value === 'object' && value !== null;

/**
 * A name and label list, such as a flow navigation's `navigationSteps`, from either CMS.
 *
 * Sitecore sends such a field's value as `{ enum: [...] }`, each entry `{ name, value, label }`, and the
 * Sitecore adapter hands that object on as it is. Sanity stores the list itself, each entry
 * `{ _key, _type, name, label }`, with `description` where the list has one. Both come out as
 * `{ name, label }`.
 *
 * An entry without a name is left out, since nothing could match it. An entry without a label keeps
 * an empty one: Sanity leaves out an empty label, where Sitecore sends it as an empty string.
 */
export const readNameLabelList = (value: unknown): NameLabelEntry[] => {
  const entries: unknown = Array.isArray(value) ? value : isObject(value) ? value.enum : undefined;
  if (!Array.isArray(entries)) return [];

  return entries.flatMap(entry =>
    isObject(entry) && typeof entry.name === 'string' && entry.name !== ''
      ? [{ name: entry.name, label: typeof entry.label === 'string' ? entry.label : '' }]
      : [],
  );
};
